import classes from "./StudioEditorPage.module.css"
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState,  useRef, useEffect, useContext } from "react";
import ProfileMenu from "../../components/NavBar/ProfileMenu";
import CreatorNavBar from "../../components/NavBar/CreatorNavBar/CreatorNavBar";
import Overview from "../../components/StudioEditorPage/Overview";
import { useStudio } from "../../hooks/useStudio";
import { StudioEditorProvider } from "../../store/studioEditorStore";
import ModesSwitch from "../../components/UI/ModesSwitch";
import { NavBarContext } from '../../App';
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";

import EditorContainer from "../../components/StudioEditorPage/EditorContainer";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';

const UPDATES_MODAL_EDITOR_ENTERED_KEY = 'stu_updates_modal_editor_entered';

export const StudioEditorPage = () => {
    const [isMobileView, setIsMobileView] = useState(false);
    const [navigationHeaderPosition, setNavigationHeaderPosition] = useState(0);
    const navigationHeaderRef = useRef(null);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const userContainerRef = useRef(null);
    const { profilePicture, lastName, firstName, _id: userId } = useSelector((state) => state.auth.user);
    const profileMenuRef = useRef(null);
    const { id } = useParams();
    const [activeComponent, setActiveComponent] = useState(null);
    const [dirtyEditors, setDirtyEditors] = useState({});
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [isSavingBeforeLeave, setIsSavingBeforeLeave] = useState(false);
    const pendingNavigationRef = useRef(null);
    const editorSaveHandlerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { activeView, setActiveView } = useContext(NavBarContext);
    
    const { studio: data, loading: isLoading, error: studioError } = useStudio(id, { includeExact: true });
    const hasUnsavedEditorChanges = Object.values(dirtyEditors).some(Boolean);

    const requestNavigationWithGuard = (navigationAction) => {
      if (hasUnsavedEditorChanges) {
        pendingNavigationRef.current = navigationAction;
        setShowLeaveModal(true);
        return;
      }
      navigationAction();
    };

    const handleMessagesClick = () => {
      setActiveView('creator');
      navigate('/messages');
  };
    
    useEffect(() => {
      if (location?.hash) {
          const componentId = location?.hash?.substring(1);
          setActiveComponent(componentId);
      } else {
          if (window.innerWidth > 1071) {
              setActiveComponent("titel");
          } else {
              setActiveComponent(null);
          }
      }
    }, [location?.hash]);

    useEffect(() => {
      if (!userId) {
        return;
      }

      localStorage.setItem(`${UPDATES_MODAL_EDITOR_ENTERED_KEY}_${userId}`, 'true');
    }, [userId]);

    const handleBackClick = () => {
      requestNavigationWithGuard(() => {
        if (location.hash) {
          navigate(`/services-studios/editor/${id}`);
        } else {
          navigate(`/services-studios`);
        }
        setActiveComponent(null);
      });
    };

    const handlePreviewClick = () => {
      requestNavigationWithGuard(() => {
        navigate(`/preview/studio/${id}`, {
          state: { from: "editor" },
        });
      });
    };

    const handleProfileMenuClick = () => {
        setShowProfileMenu(prev => !prev);
    };

    const handleCardClick = (componentId) => {
      const nextAction = () => {
        setActiveComponent(componentId);
        setIsMobileView(true);
        navigate(`/services-studios/editor/${id}#${componentId}`);
        if (window.innerWidth < 768) {
            document?.querySelector(`.${classes.editorContainer}`)?.scrollIntoView({ behavior: 'smooth' });
        }
      };
      if (componentId === activeComponent) {
        nextAction();
        return;
      }
      requestNavigationWithGuard(nextAction);
    };

    const handleDirtyStateChange = (componentId, isDirty) => {
      setDirtyEditors((prev) => {
        if (!isDirty && !prev[componentId]) {
          return prev;
        }
        return { ...prev, [componentId]: isDirty };
      });
    };

    const cancelLeaveModal = () => {
      setShowLeaveModal(false);
      pendingNavigationRef.current = null;
    };

    const leaveWithoutSaving = () => {
      const next = pendingNavigationRef.current;
      setShowLeaveModal(false);
      pendingNavigationRef.current = null;
      setDirtyEditors({});
      if (next) next();
    };

    const saveAndLeave = async () => {
      const next = pendingNavigationRef.current;
      if (!next) {
        setShowLeaveModal(false);
        return;
      }
      try {
        setIsSavingBeforeLeave(true);
        const saveFn = editorSaveHandlerRef.current;
        const saveOk = typeof saveFn === "function" ? await saveFn() : true;
        if (!saveOk) return;
        setShowLeaveModal(false);
        pendingNavigationRef.current = null;
        next();
      } finally {
        setIsSavingBeforeLeave(false);
      }
    };

    useEffect(() => {
      const handleBeforeUnload = (event) => {
        if (!hasUnsavedEditorChanges) return;
        event.preventDefault();
        event.returnValue = "";
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedEditorChanges]);

    useEffect(() => {
          const updateNavigationPosition = () => {
              if (navigationHeaderRef.current) {
                  const rect = navigationHeaderRef.current.getBoundingClientRect();
                  setNavigationHeaderPosition(rect.left);
              }
          };

          // Initial position calculation
          updateNavigationPosition();

          // Update on window resize
          window.addEventListener('resize', updateNavigationPosition);
          
          return () => {
              window.removeEventListener('resize', updateNavigationPosition);
          };
    }, []);
    

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (isHelpMenuPanelTarget(event.target)) {
          return;
        }

        if (
          profileMenuRef.current && 
          !profileMenuRef.current.contains(event.target) &&
          !userContainerRef.current?.contains(event.target)
        ) {
          setShowProfileMenu(false);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
  
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [window.innerWidth]);  
  
    return (
      <StudioEditorProvider studio={data}>
        <div className={classes.studioEditorPage}>
          <header className={classes.header}>
            <Link to="/profile" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                    <g clipPath="url(#clip0_8485_11701)">
                    <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                    <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                    <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_8485_11701">
                            <rect width="51.0642" height="17.6761" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <CreatorNavBar 
                onMessagesClick={handleMessagesClick}
                onBackClick={handleBackClick}
            />
            <div className={classes.headerContent}>
              <ModesSwitch />
              <div 
                    ref={userContainerRef} 
                    onClick={handleProfileMenuClick} 
                    className={classes.userContainer}
                  >
                  <div className={classes.userImageContainer}>
                    <img src={profilePicture} alt="User Profile" /> 
                  </div>
              </div>
            </div>
          </header>
          <div className={classes.contentContainer}>  
            <Overview 
              studio={data} 
              onCardClick={handleCardClick} 
              isMobileView={isMobileView && activeComponent}
              handleBackClick={handleBackClick}
              onPreviewClick={handlePreviewClick}
              setActiveComponent={setActiveComponent}
            />
            <EditorContainer 
              className={classes.editorContainer}
              studio={data} 
              activeComponent={activeComponent} 
              leftPosition={navigationHeaderPosition}
              isMobileView={isMobileView && activeComponent}
              handleBackClick={handleBackClick}
              onEditorDirtyChange={handleDirtyStateChange}
              registerSaveHandler={(saveHandler) => {
                editorSaveHandlerRef.current = saveHandler;
              }}
            />
          </div>
          <footer className={classes.footer}>
          </footer>
          {showProfileMenu && (
          <ProfileMenu
            ref={profileMenuRef}
            profilePicture={profilePicture}
            firstName={firstName}
            lastName={lastName}
            setShowProfileMenu={setShowProfileMenu}
            //fromProfile={fromProfile}
            />
          )}
          {!activeComponent && (
            <div className={classes.mobileNavBar}>
              <NavBarPhone fromServicesStudios={true}/>
            </div>
          )}
          <SmallModal open={showLeaveModal} onBackdropClick={cancelLeaveModal} fromDeleteStudio={true}>
            <div className={classes.leaveConfirmModal} onClick={(e) => e.stopPropagation()}>
              <button className={classes.closeButton} onClick={cancelLeaveModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </button>
              <h2 className={`${classes.leaveConfirmTitle} titleSemibold3`}>Änderungen speichern?</h2>
              <p className={`${classes.leaveConfirmText} regularLarge`}>
                Deine Änderungen gehen verloren. Vor dem Verlassen speichern?
              </p>
              <div className={classes.leaveConfirmButtons}>
                <button
                  type="button"
                  onClick={saveAndLeave}
                  disabled={isSavingBeforeLeave}
                  className={`${classes.leaveConfirmSaveButton} mediumLarge`}
                >
                  {isSavingBeforeLeave ? "Wird gespeichert..." : "Änderungen speichern"}
                </button>
                <button
                  type="button"
                  onClick={leaveWithoutSaving}
                  disabled={isSavingBeforeLeave}
                  className={`${classes.leaveConfirmDiscardButton} mediumLarge`}
                >
                  Nicht speichern
                </button>
              </div>
            </div>
          </SmallModal>
        </div>
      </StudioEditorProvider>
  );
};

export default StudioEditorPage;
